import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { I18nProvider } from './context/I18nContext';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Dashboard from './pages/Dashboard';
import CallLog from './pages/CallLog';
import CallDetail from './pages/CallDetail';
import Analytics from './pages/Analytics';
import QualityDashboard from './pages/QualityDashboard';
import SettingsPage from './pages/SettingsPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminCompanies from './pages/AdminCompanies';
import AdminCompanyDetail from './pages/AdminCompanyDetail';
import AdminCalls from './pages/AdminCalls';
import AdminUsers from './pages/AdminUsers';
import AdminFinance from './pages/AdminFinance';
import AdminSettings from './pages/AdminSettings';
import AdminIntegrations from './pages/AdminIntegrations';
import Contacts from './pages/Contacts';
import ContactDetail from './pages/ContactDetail';
import BuildQueue from './pages/BuildQueue';
import BillingPage from './pages/BillingPage';
import AgentConfigPage from './pages/AgentConfigPage';
import LeadBoard from './pages/LeadBoard';

function ProtectedRoute({ children, adminOnly, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-navy-900 flex items-center justify-center"><div className="w-8 h-8 border-2 border-accent-blue border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  if (roles && roles.length && !roles.includes(user.role)) {
    // Send admins back to admin dashboard; everyone else to client dashboard
    return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace />;
  }
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-navy-900 flex items-center justify-center"><div className="w-8 h-8 border-2 border-accent-blue border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace /> : <LoginPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/calls" element={<ProtectedRoute><CallLog /></ProtectedRoute>} />
      <Route path="/calls/:callId" element={<ProtectedRoute><CallDetail /></ProtectedRoute>} />
      <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
      <Route path="/contacts/:contactId" element={<ProtectedRoute><ContactDetail /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute><LeadBoard /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/quality" element={<ProtectedRoute><QualityDashboard /></ProtectedRoute>} />
      {/* /roi is now part of Analytics */}
      <Route path="/roi" element={<Navigate to="/analytics" replace />} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/companies" element={<ProtectedRoute adminOnly><AdminCompanies /></ProtectedRoute>} />
      <Route path="/admin/companies/:companyId" element={<ProtectedRoute adminOnly><AdminCompanyDetail /></ProtectedRoute>} />
      <Route path="/admin/calls" element={<ProtectedRoute adminOnly><AdminCalls /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/integrations" element={<ProtectedRoute adminOnly><AdminIntegrations /></ProtectedRoute>} />
      <Route path="/admin/finance" element={<ProtectedRoute adminOnly><AdminFinance /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute>} />
      <Route path="/build-queue" element={<ProtectedRoute adminOnly><BuildQueue /></ProtectedRoute>} />
      {/* Client-facing pages — MANAGER + legacy CLIENT (admins manage a company's billing/agent via /admin/companies/:id) */}
      <Route path="/billing" element={<ProtectedRoute roles={['manager', 'client']}><BillingPage /></ProtectedRoute>} />
      <Route path="/agent-config" element={<ProtectedRoute roles={['manager', 'client']}><AgentConfigPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ToastProvider>
          <AuthProvider>
            <I18nProvider>
              <ErrorBoundary>
                <AppRoutes />
              </ErrorBoundary>
            </I18nProvider>
          </AuthProvider>
        </ToastProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
